/**
 * F60 H2b (PLAN-MEHRHAUS-20261026 §8): reine Regeln für Häuser in der Oberfläche.
 *
 * F60-2: Die Haus-Wahl im Projektrahmen und die Spalte „Haus“ erscheinen erst ab ZWEI AKTIVEN Häusern. Bei einem Haus
 * sieht jede Seite aus wie vorher; ohne Haus-Wahl fragt keine Seite mit `?haus=` an.
 */

/** Nur die Felder, die die Regeln brauchen (passt zu `Haus` aus api.ts). */
export interface HausKurz {
  nummer: number;
  isActive: boolean;
}

/** Ab zwei aktiven Häusern ist ein Projekt ein Mehrhausprojekt (F60-2). */
export function istMehrhaus(haeuser: readonly HausKurz[] | null | undefined): boolean {
  return (haeuser ?? []).filter((h) => h.isActive).length >= 2;
}

/** „Haus 02“ – zweistellig wie auf dem Server (`Hausspalte.Text`). */
export function hausText(nummer: number): string {
  return `Haus ${String(nummer).padStart(2, '0')}`;
}

/** Abfrageteil für Räume/Türen: `?haus=2` oder leer (ohne Wahl wie bisher). */
export function hausAbfrage(haus: number | null | undefined): string {
  return haus == null ? '' : `?haus=${haus}`;
}

/**
 * Gruppenschlüssel der Geschossgliederung in Raumbuch und Türbuch: im Mehrhausprojekt „Haus 02 · E0“, sonst das
 * Geschoss allein wie bisher. Zweistellig, damit die Ordnung nach Text Haus vor Geschoss sortiert.
 */
export function ebeneMitHaus(
  ebene: string, hausNummer: number | null | undefined, mehrhaus: boolean,
): string {
  return mehrhaus && hausNummer != null ? `${hausText(hausNummer)} · ${ebene}` : ebene;
}

/**
 * Die Haus-Wahl gilt nur, solange das Haus existiert und aktiv ist und das Projekt ein Mehrhausprojekt bleibt; sonst
 * „Alle“ (null). So fragt eine Seite nie mit einem Haus an, das der Server mit 404 beantwortet.
 */
export function gueltigeWahl(
  wahl: number | null, haeuser: readonly HausKurz[] | null | undefined,
): number | null {
  if (wahl == null || !istMehrhaus(haeuser)) return null;
  return (haeuser ?? []).some((h) => h.isActive && h.nummer === wahl) ? wahl : null;
}

/** Nächste freie Hausnummer (1..99) für „Haus anlegen“, oder null, wenn alle vergeben sind. */
export function naechsteNummer(haeuser: readonly HausKurz[]): number | null {
  const belegt = new Set(haeuser.map((h) => h.nummer));
  for (let n = 1; n <= 99; n++) if (!belegt.has(n)) return n;
  return null;
}

/** Warnung bei mehr als einer Raum- oder Türbuchquelle je Haus (erlaubt, Bauabschnitte; F60 H2). */
export function quellenWarnung(
  h: { raumbuchquellen: number; tuerbuchquellen: number },
): string | null {
  const teile: string[] = [];
  if (h.raumbuchquellen > 1) teile.push(`${h.raumbuchquellen} Raumbuchquellen`);
  if (h.tuerbuchquellen > 1) teile.push(`${h.tuerbuchquellen} Türbuchquellen`);
  return teile.length ? teile.join(', ') : null;
}
